<template>
<div>
    <b-tabs pills content-class="mt-1">

        <!-- ── Tab 1: Client Information ── -->
        <b-tab active>
            <template #title>
                <feather-icon icon="UserIcon" size="14" class="mr-50" />
                Client Information
            </template>

    <b-card-actions action-collapse title="Client Information">
        <validation-observer ref="simpleRules">
            <b-form>

                <!-- ─── PROFILE ─── -->
                <p class="section-label">Profile</p>
                <b-row>
                    <b-col cols="12" class="mb-2">
                        <div class="d-flex align-items-center">
                            <b-img
                                :src="avatarSrc"
                                height="80"
                                width="80"
                                rounded="circle"
                                class="mr-2 avatar-preview"
                            />
                            <div>
                                <small class="text-muted d-block mb-50">Recommended 200×200 · JPG, PNG, GIF</small>
                                <b-form-file
                                    v-model="input.userFile"
                                    accept=".jpg, .png, .gif"
                                    placeholder="Choose file"
                                    size="sm"
                                />
                            </div>
                        </div>
                    </b-col>

                    <b-col md="3">
                        <b-form-group label="First Name *">
                            <validation-provider #default="{ errors }" rules="required" name="firstname">
                                <b-form-input v-model="input.firstname" placeholder="First Name" :state="errors.length ? false : null" />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-col>

                    <b-col md="3">
                        <b-form-group label="Last Name">
                            <validation-provider #default="{ errors }" rules="min:0" name="lastname">
                                <b-form-input v-model="input.lastname" placeholder="Last Name" />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-col>
                    <b-col md="2">
                        <b-form-group label="Nickname">
                            <b-form-input v-model="input.nickname" placeholder="Nickname" />
                        </b-form-group>
                    </b-col>
                    <b-col md="2">
                        <b-form-group label="Group *">
                            <validation-provider #default="{ errors }" rules="required" name="type_id">
                                <v-select
                                    v-model="input.type_id"
                                    :reduce="(option) => option.id"
                                    :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
                                    label="name"
                                    :options="groups"
                                />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-col>
                    <b-col md="1">
                        <b-form-group label="Active">
                            <b-form-checkbox
                                v-model="input.is_active"
                                class="custom-control-primary mt-50"
                                switch
                            />
                        </b-form-group>
                    </b-col>

                </b-row>

                <hr class="my-2">

                <!-- ─── CONTACT ─── -->
                <p class="section-label">Contact</p>
                <b-row>
                    <!-- Emails -->
                    <b-col md="6">
                        <b-form-group label="Email">
                            <draggable v-model="input.emails" tag="div" handle=".handle" v-if="input.emails.length > 0" class="mb-1">
                                <div
                                    v-for="(em, index) in input.emails"
                                    :key="'email-'+index"
                                    class="contact-item d-flex align-items-center mb-50"
                                >
                                    <feather-icon v-if="editingEmailIndex !== index" class="handle cursor-pointer text-muted mr-1" icon="MenuIcon" size="16" />
                                    <template v-if="editingEmailIndex !== index">
                                        <span class="flex-grow-1 text-truncate">{{ em }}</span>
                                        <b-badge variant="light-primary" class="cursor-pointer ml-50" @click="startEditEmail(index)">
                                            <feather-icon icon="Edit2Icon" size="12" />
                                        </b-badge>
                                        <b-badge variant="light-danger" class="cursor-pointer ml-25" @click="removeEmail(index)">
                                            <feather-icon icon="XIcon" size="12" />
                                        </b-badge>
                                    </template>
                                    <template v-else>
                                        <b-form-input
                                            v-model="editEmailValue"
                                            size="sm"
                                            class="flex-grow-1 mr-50"
                                            @keyup.enter="saveEditEmail(index)"
                                            @keyup.esc="cancelEditEmail"
                                        />
                                        <b-badge variant="light-success" class="cursor-pointer mr-25" @click="saveEditEmail(index)">
                                            <feather-icon icon="CheckIcon" size="12" />
                                        </b-badge>
                                        <b-badge variant="light-secondary" class="cursor-pointer" @click="cancelEditEmail">
                                            <feather-icon icon="XIcon" size="12" />
                                        </b-badge>
                                    </template>
                                </div>
                            </draggable>
                            <validation-provider #default="{ errors }" rules="min:0" name="emails">
                                <b-input-group>
                                    <b-form-input v-model="email" placeholder="Add email address" />
                                    <b-input-group-append>
                                        <b-button variant="outline-primary" @click="addEmail">Add</b-button>
                                    </b-input-group-append>
                                </b-input-group>
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-col>

                    <!-- Phones -->
                    <b-col md="6">
                        <b-form-group label="Phone">
                            <div v-if="input.phones.length > 0" class="mb-1">
                                <div
                                    v-for="(ph, index) in input.phones"
                                    :key="'ph-'+index"
                                    class="contact-item d-flex align-items-center mb-50"
                                    :class="{ 'contact-item--primary': ph.is_primary }"
                                >
                                    <template v-if="editingPhoneIndex !== index">
                                        <b-badge v-b-tooltip.hover title="Set as primary" :variant="ph.is_primary ? 'primary' : 'light-secondary'" class="cursor-pointer mr-25" @click="setPrimaryPhone(index)">
                                            <feather-icon icon="StarIcon" size="11" />
                                        </b-badge>
                                        <b-badge v-b-tooltip.hover title="WhatsApp" :variant="ph.is_whatsapp ? 'success' : 'light-secondary'" class="cursor-pointer mr-50" @click="toggleWhatsapp(index)">
                                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="12" height="12" fill="currentColor">
                                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                            </svg>
                                        </b-badge>
                                        <div class="flex-grow-1">
                                            <span class="mr-25" style="font-size:1rem;line-height:1">{{ getPhoneIcon(ph) }}</span>
                                            <span class="font-weight-bold">{{ ph.phone_no }}</span>
                                            <small class="text-muted ml-50" v-if="ph.phone_name">{{ ph.phone_name }}</small>
                                            <small class="text-muted ml-25" v-if="ph.phone_label">· {{ ph.phone_label }}</small>
                                        </div>
                                        <b-badge variant="light-primary" class="cursor-pointer mr-25" @click="startEditPhone(index)">
                                            <feather-icon icon="Edit2Icon" size="11" />
                                        </b-badge>
                                        <b-badge variant="light-danger" class="cursor-pointer" @click="removePhone(index)">
                                            <feather-icon icon="XIcon" size="11" />
                                        </b-badge>
                                    </template>
                                    <template v-else>
                                        <b-form-input v-model="editPhoneNo" size="sm" placeholder="Phone number" class="mr-25" style="flex:2" @keyup.enter="saveEditPhone(index)" @keyup.esc="cancelEditPhone" />
                                        <b-form-select v-model="editPhoneName" :options="phoneTypeOptions" size="sm" class="mr-25" style="flex:0.8" />
                                        <b-form-input v-model="editPhoneLabel" size="sm" placeholder="Name" class="mr-50" style="flex:1" @keyup.enter="saveEditPhone(index)" @keyup.esc="cancelEditPhone" />
                                        <b-badge variant="light-success" class="cursor-pointer mr-25" @click="saveEditPhone(index)">
                                            <feather-icon icon="CheckIcon" size="11" />
                                        </b-badge>
                                        <b-badge variant="light-secondary" class="cursor-pointer" @click="cancelEditPhone">
                                            <feather-icon icon="XIcon" size="11" />
                                        </b-badge>
                                    </template>
                                </div>
                            </div>
                            <validation-provider #default="{ errors }" rules="min:0" name="phone">
                                <b-input-group class="mb-50">
                                    <b-form-input v-model="phone_no" placeholder="Phone number" />
                                    <b-form-select v-model="phone_name" :options="phoneTypeOptions" style="max-width:110px" />
                                    <b-form-input v-model="phone_label" placeholder="Name (optional)" style="max-width:140px" />
                                    <b-input-group-append>
                                        <b-button variant="outline-primary" @click="addPhone">Add</b-button>
                                    </b-input-group-append>
                                </b-input-group>
                                <b-form-checkbox v-model="phone_is_whatsapp" class="mt-25">
                                    <small>This number uses WhatsApp</small>
                                </b-form-checkbox>
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-col>
                </b-row>

                <hr class="my-2">

                <!-- ─── MESSAGING ─── -->
                <p class="section-label">Messaging</p>
                <b-row>
                    <b-col md="6">
                        <b-form-group label="LINE ID">
                            <validation-provider #default="{ errors }" rules="min:0" name="line_contact">
                                <b-input-group>
                                    <b-input-group-prepend is-text>
                                        <feather-icon icon="MessageCircleIcon" />
                                    </b-input-group-prepend>
                                    <b-form-input v-model="input.line_contact" placeholder="LINE ID" />
                                </b-input-group>
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-col>

                    <b-col md="6">
                        <!-- <b-form-group label="WhatsApp">
                            <validation-provider #default="{ errors }" rules="min:0" name="whatsapp">
                                <b-input-group>
                                    <b-input-group-prepend is-text>
                                        <feather-icon icon="PhoneCallIcon" />
                                    </b-input-group-prepend>
                                    <b-form-input v-model="input.whatsapp" placeholder="WhatsApp number" />
                                </b-input-group>
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group> -->
                    </b-col>
                </b-row>

                <hr class="my-2">

                <!-- ─── ADDITIONAL ─── -->
                <p class="section-label">Additional</p>
                <b-row>
                    <b-col md="6">
                        <b-form-group label="Address">
                            <validation-provider #default="{ errors }" rules="min:0" name="address">
                                <b-form-textarea v-model="input.address" placeholder="Address" rows="3" />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-col>

                    <b-col md="6">
                        <b-form-group label="Comment">
                            <validation-provider #default="{ errors }" rules="min:0" name="comment">
                                <b-form-textarea v-model="input.comment" placeholder="Comment" rows="3" />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-col>

                    <b-col md="3">
                        <b-form-group label="Country">
                            <validation-provider #default="{ errors }" rules="min:0" name="country">
                                <b-form-input v-model="input.country" placeholder="Country" />
                                <small class="text-danger">{{ errors[0] }}</small>
                            </validation-provider>
                        </b-form-group>
                    </b-col>

                    <b-col md="3">
                        <b-form-group label="Preferred Language">
                            <v-select
                                v-model="input.preferred_language"
                                :options="languageOptions"
                                :reduce="opt => opt.value"
                                label="label"
                                placeholder="Select language"
                            />
                        </b-form-group>
                    </b-col>
                </b-row>

                <hr class="my-2">

                <b-button variant="primary" type="submit" @click.prevent="validationForm">
                    <feather-icon icon="SaveIcon" class="mr-50" size="14" />
                    Save
                </b-button>

            </b-form>
        </validation-observer>
    </b-card-actions>

        </b-tab>

        <!-- ── Tab 2: Properties ── -->
        <b-tab v-if="id != undefined" lazy>
            <template #title>
                <feather-icon icon="HomeIcon" size="14" class="mr-50" />
                Properties
            </template>
            <property-lists-owner :preset-id="id" />
        </b-tab>

        <!-- ── Tab 3: Enquiries ── -->
        <b-tab v-if="id != undefined" lazy>
            <template #title>
                <feather-icon icon="MessageSquareIcon" size="14" class="mr-50" />
                Enquiries
            </template>
            <enquiry-list-client :preset-id="id" />
        </b-tab>

    </b-tabs>
</div>
</template>

<script>
import { VueAutosuggest } from 'vue-autosuggest'
import vSelect from 'vue-select'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import {
  BFormInput, BFormGroup, BFormSelect, BInputGroup, BInputGroupAppend, BInputGroupPrepend, BForm, BBadge,
  BRow, BCol, BButton, BFormTextarea, BFormCheckbox, BFormFile, BImg, BLink,
  BCard, BMedia, BAvatar, BCardText, BMediaAside, BMediaBody, BFormTags, BContainer,
  BTabs, BTab,
  VBTooltip
} from 'bootstrap-vue'
import {
  required, email, confirmed, url, between, alpha, integer, password, min, digits, alphaDash, length,
} from '@validations'
import BCardActions from '../../../../@core/components/b-card-actions/BCardActions.vue'
import axios from '@axios'
import PropertyListsOwner from '@/views/properties/property-list/PropertyListsOwner.vue'
import EnquiryListClient from '@/views/enquiry/enquiry-list/EnquiryListClient.vue'
import draggable from 'vuedraggable'
import Ripple from 'vue-ripple-directive'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { ref, onUnmounted } from '@vue/composition-api'
import router from '@/router'
import store from "@/store";
import clientStoreModule from '../clientStoreModule'

export default {
    components: {
        BCardActions,
        BBadge,
        BFormSelect,
        BInputGroup, BInputGroupAppend, BInputGroupPrepend,
        VueAutosuggest,
        ValidationProvider,
        ValidationObserver,
        BFormInput,
        BFormGroup,
        BForm,
        BRow,
        BCol,
        BButton,
        BFormTextarea,
        vSelect,
        BFormCheckbox,
        BCard, BMedia, BAvatar, BCardText, BMediaAside, BMediaBody, BImg, BFormFile, BLink,
        BFormTags,
        PropertyListsOwner,
        EnquiryListClient,
        draggable,
        BContainer,
        BTabs,
        BTab
    },

    directives: {
        Ripple,
        'b-tooltip': VBTooltip,
    },

    props: {
        presetId: {
            type: [String, Number],
            default: null,
        },
    },

    data() {
        return {
            id: this.presetId || router.currentRoute.params.id,
            email: '',
            phone_no: '',
            phone_name: 'Mobile',
            phone_label: '',
            phone_is_whatsapp: false,
            editingEmailIndex: null,
            editEmailValue: '',
            editingPhoneIndex: null,
            editPhoneNo: '',
            editPhoneName: 'Mobile',
            editPhoneLabel: '',
            phoneTypeOptions: ['Mobile', 'Home', 'Office', 'Other'],
            input: {
                email: null,
                emails: [],
                phone_no: null,
                phone_name: null,
                phones: [],
                password: null,
                firstname: null,
                lastname: null,
                nickname: null,
                comment: null,
                address: null,
                country: null,
                preferred_language: null,
                is_active: true,
                userFile: null,
                type_id: null,
                avatar_pathurl: null,
                avatar_url: null,
                role: 'user',
                line_contact: null,
                whatsapp: null,
            },
            passwordCon: null,
            userEdit: {},
            emails: [],
            phones: [],
            groups: [],
            languageOptions: [
                { label: 'Thai', value: 'th' },
                { label: 'English', value: 'en' },
                { label: 'Chinese (Mandarin)', value: 'zh' },
                { label: 'Japanese', value: 'ja' },
                { label: 'Korean', value: 'ko' },
                { label: 'Russian', value: 'ru' },
                { label: 'French', value: 'fr' },
                { label: 'German', value: 'de' },
                { label: 'Arabic', value: 'ar' },
            ],
        }
    },

    setup() {
        const CLIENT_STORE_MODULE_NAME = "app-client";
        if (!store.hasModule(CLIENT_STORE_MODULE_NAME)) store.registerModule(CLIENT_STORE_MODULE_NAME, clientStoreModule);
        onUnmounted(() => {
            if (store.hasModule(CLIENT_STORE_MODULE_NAME)) store.unregisterModule(CLIENT_STORE_MODULE_NAME);
        });

        const previewSrc = ref(null)

        return {
            previewSrc,
        }
    },

    computed: {
        avatarSrc() {
            return this.previewSrc || this.input.avatar_url || this.input.avatar_pathurl || "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 80 80'%3E%3Ccircle cx='40' cy='40' r='40' fill='%23ebe9f1'/%3E%3Ccircle cx='40' cy='30' r='13' fill='%23b8b5c3'/%3E%3Cpath d='M14,72 Q14,50 40,50 Q66,50 66,72' fill='%23b8b5c3'/%3E%3C/svg%3E";
        },
    },

    watch: {
        '$route.params.id'(newId) {
            this.id = newId
            this.getData();
        },
        'input.userFile'(file) {
            if (file instanceof File) {
                const reader = new FileReader()
                reader.onload = e => { this.previewSrc = e.target.result }
                reader.readAsDataURL(file)
            }
        },
        phone_no(val) {
            const cleaned = val.replace(/[\s\-\(\)\.]/g, '');
            // Thai landline prefixes: 02-05, 07xx
            if (/^0[2-57]/.test(cleaned)) {
                this.phone_name = 'Home';
            } else {
                this.phone_name = 'Mobile';
            }
        },
    },

    created() {
        this.getGroups();
        if (this.id != undefined) {
            setTimeout(() => {
                this.getData();
            }, 500);
        } else {
            this.input.type_id = 3;
        }
    },

    methods: {
        removeEmail(index) {
            this.input.emails.splice(index, 1)
            if (this.editingEmailIndex === index) this.cancelEditEmail();
        },

        startEditEmail(index) {
            this.editingEmailIndex = index;
            this.editEmailValue = this.input.emails[index];
        },
        saveEditEmail(index) {
            if (this.editEmailValue.trim()) {
                this.$set(this.input.emails, index, this.editEmailValue.trim());
            }
            this.cancelEditEmail();
        },
        cancelEditEmail() {
            this.editingEmailIndex = null;
            this.editEmailValue = '';
        },

        async addEmail() {
            const res = await axios.post('users/v1/checkemail', JSON.stringify({ email: this.email }));
            if (res.data.success === true) {
                this.input.emails.push(this.email);
                this.email = '';
            } else {
                this.$toast({
                    component: ToastificationContent,
                    position: 'top-right',
                    props: { title: 'Error', icon: 'CoffeeIcon', variant: 'danger', text: res.data.message },
                })
            }
        },

        removePhone(index) {
            const ph = this.input.phones[index];
            const label = ph.phone_name ? `${ph.phone_no} (${ph.phone_name})` : ph.phone_no;
            this.$bvModal
                .msgBoxConfirm(`Remove ${label}?`, {
                    title: 'Confirm Delete',
                    size: 'sm',
                    okVariant: 'danger',
                    okTitle: 'Delete',
                    cancelTitle: 'Cancel',
                    cancelVariant: 'outline-secondary',
                    hideHeaderClose: false,
                    centered: true,
                })
                .then(confirmed => {
                    if (!confirmed) return;
                    this.input.phones.splice(index, 1);
                    if (this.input.phones.length > 0 && !this.input.phones.some(p => p.is_primary)) {
                        this.input.phones[0].is_primary = true;
                    }
                    if (this.editingPhoneIndex === index) this.cancelEditPhone();
                });
        },

        startEditPhone(index) {
            this.editingPhoneIndex = index;
            this.editPhoneNo = this.input.phones[index].phone_no;
            this.editPhoneName = this.input.phones[index].phone_name || 'Mobile';
            this.editPhoneLabel = this.input.phones[index].phone_label || '';
        },
        saveEditPhone(index) {
            if (this.editPhoneNo.trim()) {
                const isFixed = ['Home', 'Office'].includes(this.editPhoneName);
                this.$set(this.input.phones, index, {
                    ...this.input.phones[index],
                    phone_no: isFixed
                        ? this.editPhoneNo.trim().replace(/[\s\-\(\)\.]/g, '')
                        : this.formatPhoneNumber(this.editPhoneNo.trim()),
                    phone_name: this.editPhoneName,
                    phone_label: this.editPhoneLabel.trim(),
                });
            }
            this.cancelEditPhone();
        },
        cancelEditPhone() {
            this.editingPhoneIndex = null;
            this.editPhoneNo = '';
            this.editPhoneName = 'Mobile';
            this.editPhoneLabel = '';
        },

        formatPhoneNumber(phone) {
            const cleaned = phone.replace(/[\s\-\(\)\.]/g, '');
            if (cleaned.startsWith('+')) return cleaned;
            if (cleaned.startsWith('00')) return '+' + cleaned.substring(2);
            if (cleaned.startsWith('0')) return '+66' + cleaned.substring(1);
            return cleaned;
        },

        getPhoneIcon(ph) {
            if (ph.phone_name === 'Home') return '🏠';
            if (ph.phone_name === 'Office') return '🏢';
            return this.getPhoneFlag(ph.phone_no);
        },

        getPhoneFlag(phoneNo) {
            if (!phoneNo) return '';
            const n = phoneNo.replace(/\s/g, '');
            const map = [
                ['+66',  '🇹🇭'], ['+1',   '🇺🇸'], ['+44',  '🇬🇧'],
                ['+81',  '🇯🇵'], ['+82',  '🇰🇷'], ['+86',  '🇨🇳'],
                ['+91',  '🇮🇳'], ['+60',  '🇲🇾'], ['+65',  '🇸🇬'],
                ['+62',  '🇮🇩'], ['+84',  '🇻🇳'], ['+95',  '🇲🇲'],
                ['+855', '🇰🇭'], ['+856', '🇱🇦'], ['+7',   '🇷🇺'],
                ['+49',  '🇩🇪'], ['+33',  '🇫🇷'], ['+39',  '🇮🇹'],
                ['+34',  '🇪🇸'], ['+61',  '🇦🇺'], ['+55',  '🇧🇷'],
                ['+52',  '🇲🇽'], ['+971', '🇦🇪'], ['+966', '🇸🇦'],
                ['+852', '🇭🇰'], ['+886', '🇹🇼'],
            ].sort((a, b) => b[0].length - a[0].length);
            for (const [code, flag] of map) {
                if (n.startsWith(code)) return flag;
            }
            return '🌐';
        },

        async addPhone() {
            const isFixed = ['Home', 'Office'].includes(this.phone_name);
            const formatted = isFixed
                ? this.phone_no.replace(/[\s\-\(\)\.]/g, '')
                : this.formatPhoneNumber(this.phone_no);
            const res = await axios.post('users/v1/checkphone', JSON.stringify({ phone: formatted, phone_name: this.phone_name }));
            if (res.data.success === true) {
                this.input.phones.push({
                    phone_no: formatted,
                    phone_name: this.phone_name,
                    phone_label: this.phone_label.trim(),
                    is_primary: this.input.phones.length === 0,
                    is_whatsapp: this.phone_is_whatsapp,
                });
                this.phone_no = '';
                this.phone_name = 'Mobile';
                this.phone_label = '';
                this.phone_is_whatsapp = false;
            } else {
                this.$toast({
                    component: ToastificationContent,
                    position: 'top-right',
                    props: { title: 'Error', icon: 'CoffeeIcon', variant: 'danger', text: res.data.message },
                })
            }
        },

        setPrimaryPhone(index) {
            const phones = this.input.phones.map((p, i) => ({ ...p, is_primary: i === index }));
            const [primary] = phones.splice(index, 1);
            phones.unshift(primary);
            this.input.phones = phones;
        },

        toggleWhatsapp(index) {
            this.$set(this.input.phones, index, {
                ...this.input.phones[index],
                is_whatsapp: !this.input.phones[index].is_whatsapp,
            });
        },

        async getGroups() {
            const res = await axios.get('users/v1/group-types');
            this.groups = res.data.data;
        },

        async getData() {
            let user = []
            await axios
                .get('users/v1/detail/' + this.id)
                .then(response => { user = response.data.data })
                .catch((error) => {
                    this.$toast({
                        component: ToastificationContent,
                        props: {
                            title: 'Error fetching User',
                            icon: 'AlertTriangleIcon',
                            variant: 'danger',
                            text: `${error?.response?.data?.message ?? 'Something went wrong'}`,
                        },
                    })
                })

            if (!user.id) {
                this.$router.push('/clients/lists', () => {
                    this.$toast({
                        component: ToastificationContent,
                        position: 'top-right',
                        props: { title: 'Error', icon: 'CoffeeIcon', variant: 'danger', text: 'Data not Found.' },
                    })
                });
            } else {
                this.input.type_id = user.type_id;
                this.input.email = user.email;
                this.input.phone = user.phone;
                this.input.firstname = user.firstname || user.firstname;
                this.input.lastname = user.lastname || user.lastname;
                this.input.nickname = user.nickname;
                this.input.comment = user.comment;
                this.input.address = user.address;
                this.input.country = user.country;
                this.input.preferred_language = user.preferred_language;
                this.input.line_contact = user.line_contact;
                this.input.whatsapp = user.whatsapp;
                this.input.emails = user.emails.map(item => item.email);
                this.input.phones = user.phones.map(p => {
                    const isKnownType = this.phoneTypeOptions.includes(p.phone_name);
                    return {
                        ...p,
                        phone_name: isKnownType ? p.phone_name : 'Mobile',
                        phone_label: p.phone_label || (!isKnownType ? p.phone_name : '') || '',
                        is_primary: p.is_primary || false,
                        is_whatsapp: p.is_whatsapp || false,
                    };
                });
                if (this.input.phones.length > 0 && !this.input.phones.some(p => p.is_primary)) {
                    this.input.phones[0].is_primary = true;
                }
                this.input.is_active = user.is_active == 1 ? true : false;
                this.input.password = null;
                this.input.avatar_pathurl = user.avatar_pathurl;
                this.input.avatar_url = user.avatar_url;
                this.emails = user.emails;
                this.phones = user.phones;
            }
        },

        makeToast(variant = null, title, message) {
            this.$bvToast.toast(message, { title, variant, solid: true })
        },

        validationForm() {
            const userStorage = JSON.parse(localStorage.getItem('userData'))
            this.$refs.simpleRules.validate().then(success => {
                if (success) {
                    if (this.input.phones && this.input.phones.length) {
                        if (!this.input.phones.some(p => p.is_primary)) {
                            this.input.phones[0].is_primary = true;
                        }
                        this.input.phone = this.input.phones.find(p => p.is_primary);
                    } else {
                        this.input.phone = '';
                    }

                    if (this.input.emails && this.input.emails.length) {
                        this.input.email = this.input.emails[0];
                        this.input.email_remove = this.emails.filter(itm => {
                            return this.input.emails.findIndex(item => item === itm.email) < 0;
                        });
                        this.input.email_add = this.input.emails.filter(itm => {
                            return this.emails.findIndex(item => item.email === itm) < 0;
                        });
                    }

                    const formData = new FormData();
                    if (this.input.userFile instanceof File) {
                        formData.append('imageFile', this.input.userFile)
                    }
                    const { userFile, ...inputData } = this.input
                    formData.append('data', JSON.stringify(inputData))

                    if (this.id > 0) {
                        this.input.updated_by = userStorage.id;
                        store.dispatch("app-client/updateUser", { id: this.id, data: formData })
                            .then(() => {
                                this.$router.push('/clients/list', () => {
                                    this.$toast({
                                        component: ToastificationContent,
                                        position: 'top-right',
                                        props: { title: 'Done', icon: 'CoffeeIcon', variant: 'success', text: 'You have successfully updated.' },
                                    })
                                });
                            })
                            .catch(error => {
                                this.makeToast('danger', 'Error 403', `${error?.response?.data?.message ?? 'Something went wrong'}`)
                            })
                    } else {
                        this.input.created_by = userStorage.id;
                        store.dispatch("app-client/addUser", { data: formData })
                            .then(() => {
                                this.$router.push('/clients/list', () => {
                                    this.$toast({
                                        component: ToastificationContent,
                                        position: 'top-right',
                                        props: { title: 'Done', icon: 'CoffeeIcon', variant: 'success', text: 'You have successfully created.' },
                                    })
                                });
                            })
                            .catch(error => {
                                this.makeToast('danger', 'Error 403', `${error?.response?.data?.message ?? 'Something went wrong'}`)
                            })
                    }
                }
            });
        }
    }
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-autosuggest.scss';
@import '@core/scss/vue/libs/vue-select.scss';

.section-label {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #b9b9c3;
    margin-bottom: 1rem;
}

.avatar-preview {
    object-fit: cover;
    border: 2px solid #ebe9f1;
}

.contact-item {
    border: 1px solid #ebe9f1;
    border-radius: 6px;
    padding: 6px 10px;
    background: #fff;
    transition: border-color 0.15s;

    &--primary {
        border-color: #7367f0;
        background: #f8f7ff;
    }
}

.cursor-pointer {
    cursor: pointer;
}
</style>
